// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { Radio, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDialer } from '@/contexts/DialerContext';

/**
 * LiveTranscript, panneau "Coach IA" pendant un appel en cours.
 *
 * 2026-06-03, Refonte UX Kevin (nettoyage post-audit) :
 *  - Plus de tabs (Coach uniquement).
 *  - Plus de metriques (Talk Ratio, sentiment, WPM, signaux, DNA) :
 *    inutiles pendant un appel et ajoutent du bruit visuel.
 *  - Plus de transcript affiche (le tab "Activity" du ProspectDetailPanel
 *    a un panneau dedie pour ca apres l'appel).
 *  - Plus de battlecard separe (on garde uniquement la suggestion la
 *    plus recente du CoachingService).
 *
 * Conserve :
 *  - SSE EventSource live-transcript pour recevoir les segments + events
 *    operationnels (end, voicemail_detected).
 *  - onSegment callback (utilise par d'autres consommateurs futurs si
 *    besoin d'afficher la derniere phrase ailleurs).
 *  - Alimentation de CoachingService (segments, appendSegment, repliques
 *    prospect, submitProspectLine).
 */

// Panneau "Coach IA", UNE seule carte avec le dernier conseil.
function CoachSuggestionsPanel({ coachingService }) {
  const { t } = useTranslation('dialer');
  const [latest, setLatest] = useState(null);
  const [active, setActive] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    if (!coachingService) return;
    const unsub = coachingService.subscribe((snap) => {
      const recent = snap.recentSuggestions || [];
      setLatest(recent[0] || null);
      setActive(!!snap.active);
      setUnavailable(!!snap.unavailable);
    });
    return () => unsub && unsub();
  }, [coachingService]);

  if (!active) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-6 text-center">
        <p className="text-xs text-muted-foreground">{t('dialer:liveTranscript.coachDisabled')}</p>
      </div>
    );
  }
  if (!latest) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-6 text-center">
        <p className="text-xs text-muted-foreground">
          {unavailable ? t('dialer:liveTranscript.coachUnavailable') : t('dialer:liveTranscript.waitingForProspect')}
        </p>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto px-3 py-3">
      <div
        className="rounded-lg p-3 border"
        style={{
          background: 'transparent',
          borderColor: 'var(--vf-border, hsl(var(--border)))',
        }}
      >
        {latest.objectionLabel && (
          <p className="text-[10px] font-semibold uppercase tracking-wide mb-1 text-muted-foreground">
            {latest.objectionLabel}
          </p>
        )}
        <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>
          {latest.text}
        </p>
      </div>
    </div>
  );
}

export default function LiveTranscript({ callId, onSegment }) {
  const { t } = useTranslation(['dialer', 'common']);
  const { coachingService, connectedContact, selectedCustomerId } = useDialer();
  const [segments, setSegments] = useState([]);
  const [connected, setConnected] = useState(false);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    if (!callId) return;

    let evtSource: EventSource | null = null;
    let cancelled = false;

    const start = async () => {
      console.log('[LiveTranscript] start callId=', callId);
      try {
        const res = await fetch('/api/sse/token', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'transcript', callId }),
        });
        if (!res.ok) { console.warn('[LiveTranscript] sse/token failed status=', res.status); return; }
        const { token } = await res.json();
        if (cancelled || !token) return;
        evtSource = new EventSource(`/api/twilio/live-transcript/${callId}?token=${encodeURIComponent(token)}`);
        attachHandlers(evtSource);
      } catch (e) {
        console.error('[LiveTranscript] start error', (e as Error)?.message);
      }
    };

    const attachHandlers = (es: EventSource) => {
      es.onopen = () => { setConnected(true); };
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.event) {
            case 'segment':
              setSegments(prev => [...prev, { speaker: data.speaker, text: data.text, timestamp: data.timestamp }]);
              try { onSegment && onSegment({ speaker: data.speaker, text: data.text, timestamp: data.timestamp }); } catch { /* noop */ }
              break;
            case 'end':
            case 'no-session':
              setEnded(true);
              es.close();
              break;
            case 'voicemail_detected':
              // 2026-05-13 — Custom STT a detecte un repondeur. Le backend a
              // deja raccroche + marque l'outcome. On notifie le shell pour
              // fermer l'UI d'appel.
              try {
                window.dispatchEvent(new CustomEvent('revcall:voicemail_detected', {
                  detail: { callId: data.callId, transcript: data.transcript },
                }));
              } catch { /* ignore */ }
              break;
            // Les events ratio / sentiment / signal / battlecard / pacing
            // sont ignores depuis le nettoyage 2026-06-03 (UI dediee supprimee).
          }
        } catch { /* ignore */ }
      };
      es.onerror = () => { setConnected(false); };
    };

    start();

    return () => {
      cancelled = true;
      if (evtSource) { evtSource.close(); }
    };
  }, [callId, onSegment]);

  // Alimente le CoachingService avec TOUS les segments. Ne declenche le coach
  // que sur les segments inbound (repliques prospect). Dedup interne au service.
  const lastSubmittedIdxRef = useRef(-1);
  useEffect(() => {
    if (!callId || !coachingService) return;
    const isProspectSeg = (s) => s.speaker === 'inbound' || s.speaker === 'prospect' || s.speaker === 'customer';
    const customerId = connectedContact?.customer_id || selectedCustomerId || '';
    const prospectContext = connectedContact ? {
      prospect: {
        name: connectedContact.prospect_name,
        title: connectedContact.prospect_title,
        company: connectedContact.prospect_company,
      },
      customerId,
    } : { customerId };
    for (let i = lastSubmittedIdxRef.current + 1; i < segments.length; i++) {
      const seg = segments[i];
      coachingService.appendSegment(callId, seg);
      if (isProspectSeg(seg)) {
        coachingService.submitProspectLine(callId, seg.text, prospectContext);
      }
    }
    lastSubmittedIdxRef.current = segments.length - 1;
  }, [segments, callId, coachingService, connectedContact, selectedCustomerId]);

  return (
    <div className="bg-card overflow-hidden flex flex-col flex-1">
      {/* Header minimaliste */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b flex-shrink-0">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkles
            className="w-3.5 h-3.5"
            style={{ color: connected && !ended ? 'var(--vf-primary, hsl(var(--primary)))' : 'hsl(var(--muted-foreground))' }}
          />
          <span>{t('dialer:aiCoaching.coachTitle')}</span>
          {connected && !ended && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide flex items-center gap-1"
              style={{
                background: 'var(--vf-green-bg, hsl(var(--accent)))',
                color: 'var(--vf-green, hsl(var(--primary)))',
              }}
            >
              <Radio className="w-2 h-2 animate-pulse" />
              {t('dialer:liveTranscript.live')}
            </span>
          )}
          {ended && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">{t('common:states.completed')}</span>
          )}
        </div>
      </div>

      <CoachSuggestionsPanel coachingService={coachingService} />
    </div>
  );
}
